/**
 * 品牌活動 — 公開深度頁(不依賴 JWT,類似 Traceability / BrandFounder)
 *
 * 從 LINE「品牌活動」carousel 各 bubble 底部「閱讀完整紀錄」按鈕 → 跳這頁 by slug,
 * 用雜誌風排版完整活動文案 + 大圖。
 *
 * route: /brand/events/:slug
 * 2026-07-20 起內容從 Firestore brand_events 讀(後台「品牌活動」頁自建);
 * API 讀不到才 fallback 本檔 EVENTS dict(原 hardcode 3 篇,留著防網路 / 部署空窗)。
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { fetchBrandEvent, type BrandEventData } from "../api/brandEvent";


// ── GCS 活動圖基底(對齊 backend/flex/event_story.py GCS_BASE)──
const GCS_BASE = "https://storage.googleapis.com/yannwo-product-images/events";

// ── 活動內容字典 ──
type EventContent = {
  imageKey: string;          // GCS 圖 key,對應 backend LINE Flex bubble 同 key(4:3 hero)
  heroImageKey?: string;     // LIFF hero 圖 key override(可選 — 沒設用 imageKey;直幅原圖建議單獨上傳 portrait 版本避免 4:3 cover crop 切頭)
  heroAspect?: "4/3" | "3/4"; // LIFF hero 容器比例(默認 4:3 橫向,直幅圖設 "3/4" 對齊原比例不切)
  eyebrow: string;           // 上方小字(品牌定位)
  titleZh: string;
  titleEn: string;
  date: string;
  venue: string;             // 場地
  lead?: string;             // 開場引言(可選,放珊瑚橘邊條 quote)
  intro: string[];           // 開場兩三段
  sections?: {               // 結構化區塊(如「三盅」)
    icon?: string;
    title: string;
    body: string;
  }[];
  closing: string[];         // 收尾兩三段(品牌哲學)
  tags?: string[];           // hashtag chips
  gallery?: string[];        // 現場花絮 GCS 圖 key 陣列(1:1 縮圖,點圖開新分頁看原圖)
  relatedProduct?: {         // 對應商品(對齊 BrandTasting 機制,有 productId 就可點跳商品頁 highlight)
    name: string;
    spec: string;
    note?: string;
    productId?: string;
  };
};

const EVENTS: Record<string, EventContent> = {
  "louis-xiv": {
    imageKey: "louis-xiv",
    heroImageKey: "louis-xiv-portrait",
    heroAspect: "3/4",
    eyebrow: "Tasting Soirée · Louis XIV",
    titleZh: "路易十四限定品鑑之夜",
    titleEn: "Brand Moment · 2026",
    date: "2026",
    venue: "茹曦酒店 路易十四",
    lead: "讓養生,成為日常裡最優雅的儀式。",
    intro: [
      "於茹曦酒店「路易十四」,霽雲以一場優雅而細膩的品鑑體驗,邀請賓客重新認識東方養生的質感日常。",
      "主角,是霽雲《恆時即飲》燕窩。晶透杯盞中,盛裝細火慢燉後的溫潤口感,再以金箔輕點 — 讓每一口,都像是一場關於時間與品味的收藏。",
    ],
    closing: [
      "在藝術、美學與交流交織的夜晚,我們相信,真正高端的滋養,從來不是浮誇炫耀,而是在忙碌生活裡,依然願意留給自己一份純粹與講究。",
      "霽雲《恆時即飲》— 讓養生,成為日常裡最優雅的儀式。",
    ],
    tags: ["品鑑", "優雅", "燕窩", "即飲", "頂級保養"],
    gallery: Array.from({ length: 8 }, (_, i) => `louis-xiv-${i + 2}`),
    relatedProduct: {
      name: "霽雲・恆時即飲",
      spec: "60ml × 174 入",
      note: "全年滋養配方",
      productId: "heng_shi_ji",
    },
  },
  "malaysia-signing": {
    imageKey: "event-malaysia-signing",
    eyebrow: "International Dialogue · Malaysia",
    titleZh: "跨越國界 · 共創燕窩產業新里程碑",
    titleEn: "Malaysia Coop MOU Signing · 2026.1",
    date: "2026.1",
    venue: "馬來西亞燕農合作社",
    lead: "以信任為基礎,以品質為承諾。",
    intro: [
      "霽雲燕窩正式與「馬來西亞燕農合作社(Koperasi Penternak Burung Walit Malaysia Berhad)」簽署合作備忘錄(MOU),攜手建立更穩定、更透明的燕窩供應體系,為消費者帶來值得信賴的高品質燕窩產品。",
      "此次合作不僅是一場簽約儀式,更象徵著台灣與馬來西亞在燕窩產業鏈上的深度交流與資源整合。從源頭管理、品質檢驗到產品開發,每一個環節都將以更嚴謹的標準共同推進。",
    ],
    sections: [
      {
        title: "源頭管理",
        body: "從燕屋採摘到合作社體系內部標準化,每一片燕窩源頭清晰可循。",
      },
      {
        title: "品質檢驗",
        body: "雙方共建統一的檢驗規範,確保每批次符合一致的高品質標準。",
      },
      {
        title: "產品開發",
        body: "結合台馬兩地的滋補文化與科學標準,共同推進國際語境的產品。",
      },
    ],
    closing: [
      "我們相信,真正的好燕窩來自於對品質的堅持,以及對消費者的重視。",
      "感謝每一位合作夥伴的信任與支持 — 這不只是合作的開始,更是共同邁向下一個里程碑的重要一步。",
    ],
    tags: ["純粹", "無添加", "安心", "生活"],
    gallery: Array.from({ length: 15 }, (_, i) => `event-malaysia-signing-${i + 2}`),
  },
  "midsummer-2605": {
    imageKey: "midsummer-tasting-2605",
    eyebrow: "Midsummer Tasting Salon",
    titleZh: "仲夏養妍品鑒沙龍",
    titleEn: "Brand Moment · 2026.5.27",
    date: "2026.5.27",
    venue: "遠企飯店",
    lead: "在盛夏最柔軟的午後,讓養生,不只是滋補,而是一場關於味覺、美感與生活節奏的優雅體驗。",
    intro: [
      "霽雲以頂級燕窩為主角,透過三盅不同風味的細緻演繹,帶領賓客重新感受東方養生的層次與韻味。",
    ],
    sections: [
      {
        title: "第一盅 · 金箔燕窩",
        body: "以最純粹的姿態登場,點綴細緻金箔,品味燕窩最原始的溫潤與純淨。",
      },
      {
        title: "第二盅 · 沾三吃",
        body: "搭配芝麻醬、新鮮果醬及餅乾,在鹹香、果韻與酥脆之間,感受燕窩更多元的味覺可能。",
      },
      {
        title: "第三盅 · 燕窩雞湯",
        body: "以慢火熬煮的溫潤雞湯收尾,將滋養緩緩留在身體裡,也讓這場仲夏品鑒,多了一份沉靜與療癒。",
      },
    ],
    closing: [
      "霽雲相信,真正高端的養生,不是炫耀式的奢華,而是讓人在繁忙日常裡,依然願意溫柔地照顧自己。",
    ],
    tags: ["純粹", "無添加", "安心", "生活"],
    gallery: [
      "midsummer-tasting-2605-2",
      "midsummer-tasting-2605-3",
      "midsummer-tasting-2605-4",
      "midsummer-tasting-2605-5",
      "midsummer-tasting-2605-6",
      "midsummer-tasting-2605-7",
      "midsummer-tasting-2605-8",
      "midsummer-tasting-2605-9",
      "midsummer-tasting-2605-10",
      "midsummer-tasting-2605-11",
    ],
  },
};


// ── 統一 view(API doc / hardcode 都轉這型再 render)──
type EventView = {
  heroUrl: string;
  heroAspect: "4/3" | "3/4";
  eyebrow: string;
  titleZh: string;
  titleEn: string;
  date: string;
  venue: string;
  lead?: string;
  intro: string[];
  sections: { icon?: string; title: string; body: string }[];
  closing: string[];
  tags: string[];
  galleryUrls: string[];
  relatedProduct?: { name: string; spec: string; note?: string; productId?: string };
};

function viewFromHardcode(e: EventContent): EventView {
  return {
    heroUrl: `${GCS_BASE}/${e.heroImageKey || e.imageKey}.jpg`,
    heroAspect: e.heroAspect || "4/3",
    eyebrow: e.eyebrow,
    titleZh: e.titleZh,
    titleEn: e.titleEn,
    date: e.date,
    venue: e.venue,
    lead: e.lead,
    intro: e.intro,
    sections: e.sections || [],
    closing: e.closing,
    tags: e.tags || [],
    galleryUrls: (e.gallery || []).map((k) => `${GCS_BASE}/${k}.jpg`),
    relatedProduct: e.relatedProduct,
  };
}

function viewFromApi(d: BrandEventData): EventView {
  const rp = d.related_product;
  return {
    heroUrl: d.hero_image || d.card_image,
    heroAspect: d.hero_aspect === "3/4" ? "3/4" : "4/3",
    eyebrow: d.eyebrow,
    titleZh: d.title_zh,
    titleEn: d.title_en,
    date: d.date,
    venue: d.venue,
    lead: d.lead || undefined,
    intro: d.intro || [],
    sections: d.sections || [],
    closing: d.closing || [],
    tags: d.tags || [],
    galleryUrls: d.gallery || [],
    relatedProduct:
      rp && rp.name
        ? { name: rp.name, spec: rp.spec, note: rp.note || undefined, productId: rp.product_id || undefined }
        : undefined,
  };
}

export default function BrandEvent() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [view, setView] = useState<EventView | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    let alive = true;
    setLoading(true);
    setView(null);
    (async () => {
      // 先打 API(後台自建內容優先),讀不到 fallback 本檔 hardcode
      const apiDoc = slug ? await fetchBrandEvent(slug) : null;
      if (!alive) return;
      if (apiDoc) {
        setView(viewFromApi(apiDoc));
      } else {
        const hc = slug ? EVENTS[slug] : undefined;
        setView(hc ? viewFromHardcode(hc) : null);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-sm text-gray-mid tracking-widest">載入中…</div>
      </div>
    );
  }

  if (!view) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="text-xl font-bold text-primary-dark">活動資訊不存在</h2>
          <p className="text-sm text-gray-mid mt-2">請從 LINE 重新點選活動入口</p>
          <button
            onClick={() => navigate("/")}
            className="btn-primary mt-6 px-6"
          >
            回首頁
          </button>
        </div>
      </div>
    );
  }
  const event = view;

  return (
    <div className="min-h-screen bg-cream pb-20">
      {/* Header eyebrow */}
      <header className="bg-primary-dark text-white px-6 py-8 text-center">
        <div className="text-[10px] tracking-[0.3em] opacity-70 uppercase">
          霽雲 · 恆時珍藏
        </div>
        <div className="text-xs mt-2 opacity-75 tracking-widest uppercase">
          {event.eyebrow}
        </div>
      </header>

      {/* Hero — 默認 4:3 橫向,直幅 event 可 override hero 圖 + heroAspect="3/4" 避免切頭 */}
      <img
        src={event.heroUrl}
        alt={event.titleZh}
        className={`w-full object-cover ${event.heroAspect === "3/4" ? "aspect-[3/4]" : "aspect-[4/3]"}`}
      />

      {/* Title block */}
      <div className="px-6 pt-8 pb-2">
        <h1 className="text-2xl font-bold text-primary-dark tracking-wide">
          {event.titleZh}
        </h1>
        <div className="text-xs text-gray-mid mt-2 tracking-wider">
          {[event.titleEn, event.venue].filter(Boolean).join(" · ")}
        </div>
      </div>

      {/* Lead quote (coral 邊條) */}
      {event.lead && (
        <blockquote className="mx-6 my-6 border-l-[3px] border-coral pl-5 py-1">
          <p className="text-base leading-relaxed text-primary-dark">
            {event.lead}
          </p>
        </blockquote>
      )}

      {/* Intro paragraphs */}
      <div className="px-6 space-y-4 text-[15px] leading-loose text-charcoal/85">
        {event.intro.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {/* Sections (e.g. 三盅) */}
      {event.sections.length > 0 && (
        <div className="px-6 mt-8 space-y-5">
          {event.sections.map((s, i) => (
            <div
              key={i}
              className="bg-white rounded-lg p-5 border border-sage-light/40 shadow-[0_1px_3px_rgba(0,0,0,0.03)]"
            >
              <h3 className="text-base font-bold text-primary-dark flex items-center gap-2">
                {/* 不讀 s.icon:舊資料(後台顧問自己填的 emoji)一律改畫品牌色小圓點 */}
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-coral align-middle" aria-hidden="true" />
                <span>{s.title}</span>
              </h3>
              <p className="text-[14px] leading-loose text-charcoal/80 mt-3">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Closing paragraphs */}
      <div className="px-6 mt-8 space-y-4 text-[15px] leading-loose text-charcoal/85">
        {event.closing.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      {/* Related Product card — 對齊 BrandTasting,有 productId 就可點跳 /products#product-{id} */}
      {event.relatedProduct && (
        <div className="px-6 mt-8">
          <button
            type="button"
            disabled={!event.relatedProduct.productId}
            onClick={() => {
              if (event.relatedProduct?.productId) {
                navigate(`/products#product-${event.relatedProduct.productId}`);
              }
            }}
            className="w-full text-left bg-white border border-sage-light/40 rounded-lg p-5 shadow-[0_1px_3px_rgba(0,0,0,0.03)] enabled:active:bg-cream transition-colors disabled:cursor-default"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.3em] text-sage uppercase">
                  Related Product
                </div>
                <div className="mt-2 text-base font-bold text-primary-dark">
                  {event.relatedProduct.name}
                </div>
                <div className="text-sm text-gray-mid mt-1">
                  {event.relatedProduct.spec}
                </div>
                {event.relatedProduct.note && (
                  <div className="text-xs text-sage mt-2">
                    {event.relatedProduct.note}
                  </div>
                )}
              </div>
              {event.relatedProduct.productId && (
                <div className="text-coral text-2xl flex-shrink-0 leading-none">›</div>
              )}
            </div>
          </button>
        </div>
      )}

      {/* Tags */}
      {event.tags.length > 0 && (
        <div className="px-6 mt-8 flex flex-wrap gap-2 justify-center">
          {event.tags.map((tag) => (
            <span
              key={tag}
              className="text-[11px] tracking-widest text-sage px-3 py-1 border border-sage-light/60 rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Gallery — 現場花絮 2-col grid,點圖開新分頁看原圖 */}
      {event.galleryUrls.length > 0 && (
        <div className="px-6 mt-12">
          <div className="text-[10px] tracking-[0.3em] text-sage uppercase text-center">
            Gallery
          </div>
          <h3 className="text-base font-bold text-primary-dark text-center mt-2 mb-5 tracking-wide">
            現場花絮
          </h3>
          <div className="grid grid-cols-2 gap-2">
            {event.galleryUrls.map((url) => (
              <a
                key={url}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="block aspect-square overflow-hidden rounded-md bg-sage-light/20"
              >
                <img
                  src={url}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover active:opacity-80 transition-opacity"
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="text-center py-10 mt-4">
        <div className="text-[11px] text-gray-mid tracking-[0.3em]">
          霽雲 · 恆時珍藏
        </div>
        <div className="text-[10px] text-gray-mid mt-1 tracking-widest">
          {event.date}
        </div>
      </footer>
    </div>
  );
}
